import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PositionDetailsDrawer from './PositionDetailsDrawer';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import {
  fetchWorkforceBusinessUnits,
  formatBusinessUnitLabel,
  resolveWorkforceScope,
} from './workforceBusinessUnitUtils';
import {
  buildLookupListUrl,
  buildWorkAreaLabelMap,
  ensureWorkAreaInLookup,
  parseLookupItems,
  removeWorkAreaFromLookup,
  resolveWorkAreaLabel,
  slugifyWorkAreaCode,
} from './workforcePositionUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';

const workAreaFilter = createFilterOptions();

const defaultForm = {
  property_id: '',
  role_name: '',
  role_code: '',
  role_type: '',
  parent_role_id: '',
  work_area_code: '',
  work_area_label: '',
  department: '',
  department_code: '',
  description: '',
  default_shift_id: '',
  color_code: '',
  priority: '',
  is_active: '',
  is_salaried: '',
  effective_date: '',
  end_date: '',
  created_by: '',
  updated_by: '',
};

const slugifyRoleCode = (name) =>
  (name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const RolePosition = ({ onBack, selectedBusinessUnit }) => {
  const { toastState, showSuccess, showError, showWarning, hideToast } = useToast();
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const tenantId = localStorage.getItem('tenant_id') || '';
  const username = localStorage.getItem('username') || '';
  const {
    propertyId: selectedPropertyId,
    label: selectedBusinessUnitLabel,
  } = resolveWorkforceScope(selectedBusinessUnit);

  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState('add');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRolePositionId, setEditingRolePositionId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [workAreaOptions, setWorkAreaOptions] = useState([]);
  const [workAreaLoading, setWorkAreaLoading] = useState(false);
  const [roleMasterOptions, setRoleMasterOptions] = useState([]);
  const [roleMastersLoading, setRoleMastersLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowsPendingDelete, setRowsPendingDelete] = useState([]);
  const [workAreaDeleteOpen, setWorkAreaDeleteOpen] = useState(false);
  const [workAreaPendingDelete, setWorkAreaPendingDelete] = useState(null);
  const [workAreaDeleteLoading, setWorkAreaDeleteLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedDetailsRow, setSelectedDetailsRow] = useState(null);
  const token = localStorage.getItem('token') || '';
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const hasSelected = selectionModel.length > 0;
  const hasSingleSelection = selectionModel.length === 1;
  const selectedId = selectionModel[0];
  const selectedRows = useMemo(
    () => rows.filter((row) => selectionModel.includes(row.id)),
    [rows, selectionModel]
  );

  const authHeaders = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const workAreaLabelByCode = useMemo(() => buildWorkAreaLabelMap(workAreaOptions), [workAreaOptions]);

  const roleMasterNames = useMemo(() => {
    const names = roleMasterOptions
      .filter((item) => {
        const status = (item.status || 'ACTIVE').toString().toUpperCase();
        return status === 'ACTIVE';
      })
      .map((item) => item.roleName || item.role_name)
      .filter(Boolean);
    if (form.role_name && !names.some((name) => name.toLowerCase() === form.role_name.toLowerCase())) {
      return [...names, form.role_name];
    }
    return names;
  }, [roleMasterOptions, form.role_name]);

  const departmentNames = useMemo(() => {
    const options = departmentOptions
      .filter((item) => String(item.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
      .map((item) => ({
        name: item.departmentName || item.department_name || '',
        code: item.departmentCode || item.department_code || '',
      }))
      .filter((item) => item.name);
    const current = String(form.department || '').trim();
    if (current && !options.some((item) => item.name.toLowerCase() === current.toLowerCase())) {
      options.push({
        name: current,
        code: form.department_code || '',
      });
    }
    return options;
  }, [departmentOptions, form.department, form.department_code]);

  const businessUnitNameById = useMemo(
    () => Object.fromEntries(businessUnits.map((bu) => [bu.propertyId, formatBusinessUnitLabel(bu)])),
    [businessUnits]
  );

  const columns = useMemo(
    () => [
      { field: 'role_name', headerName: 'Job Title', width: 170, resizable: true },
      { field: 'role_code', headerName: 'Job Code', width: 120, resizable: true },
      { field: 'department', headerName: 'Department', width: 160, resizable: true },
      { field: 'department_code', headerName: 'Department Code', width: 140, resizable: true },
      { field: 'work_area_label', headerName: 'Work Area', width: 160, resizable: true },
      { field: 'role_type', headerName: 'Position Type', width: 120, resizable: true },
      { field: 'priority', headerName: 'Priority', width: 100, resizable: true },
      { field: 'is_salaried', headerName: 'Is Salaried', width: 120, resizable: true },
      { field: 'is_active', headerName: 'Is Active', width: 120, resizable: true },
    ],
    []
  );

  const mapApiRolePositionToRow = (item, nameLookup = {}, areaLabels = {}) => {
    const workAreaCode = item.workAreaCode || item.work_area_code || '';
    return {
      id: item.rolePositionId,
      role_position_uuid: item.rolePositionId,
      role_position_id: item.rolePositionId || '',
      tenant_id: item.tenantId || '',
      property_id: item.propertyId || '',
      property_name: nameLookup[item.propertyId] || '',
      role_name: item.roleName || '',
      role_code: item.roleCode || '',
      role_type: item.roleType || '',
      parent_role_id: item.parentRoleId || '',
      work_area_code: workAreaCode,
      work_area_label: resolveWorkAreaLabel(workAreaCode, areaLabels),
      department: item.department || '',
      department_code: item.departmentCode || item.department_code || '',
      description: item.description || '',
      default_shift_id: item.defaultShiftId || '',
      color_code: item.colorCode || '',
      priority: item.priority ?? '',
      is_salaried: item.isSalaried === true ? 'YES' : item.isSalaried === false ? 'NO' : '',
      is_active: item.isActive === true ? 'YES' : item.isActive === false ? 'NO' : '',
      effective_date: item.effectiveDate || '',
      end_date: item.endDate || '',
      created_at: item.createdAt || '',
      updated_at: item.updatedAt || '',
      created_by: item.createdBy || '',
      updated_by: item.updatedBy || '',
    };
  };

  const normalizeDateTime = (value) => {
    if (!value) return null;
    if (value.includes(' ')) return value;
    return `${value} 00:00:00`;
  };

  const buildPayload = (currentForm, currentRolePositionId) => ({
    tenantId,
    propertyId: currentForm.property_id || null,
    roleName: currentForm.role_name || null,
    roleCode: currentForm.role_code || null,
    roleType: currentForm.role_type || null,
    parentRoleId: currentForm.parent_role_id || null,
    workAreaCode: currentForm.work_area_code || null,
    department: currentForm.department || null,
    departmentCode: currentForm.department_code || null,
    description: currentForm.description || null,
    defaultShiftId: currentForm.default_shift_id || null,
    colorCode: currentForm.color_code || null,
    priority: currentForm.priority === '' ? null : Number(currentForm.priority),
    isSalaried: currentForm.is_salaried === '' ? null : currentForm.is_salaried === 'YES',
    isActive: currentForm.is_active === '' ? null : currentForm.is_active === 'YES',
    effectiveDate: normalizeDateTime(currentForm.effective_date),
    endDate: normalizeDateTime(currentForm.end_date),
    createdBy: currentRolePositionId ? (currentForm.created_by || username) : username,
    updatedBy: username,
    ...(currentRolePositionId ? { rolePositionId: currentRolePositionId } : {}),
  });

  useEffect(() => {
    let cancelled = false;
    const loadBusinessUnits = async () => {
      if (!tenantId) {
        setBusinessUnits([]);
        return;
      }
      try {
        const units = await fetchWorkforceBusinessUnits(tenantId);
        if (!cancelled) setBusinessUnits(units);
      } catch (error) {
        console.error('Error loading business units:', error);
        if (!cancelled) setBusinessUnits([]);
      }
    };
    loadBusinessUnits();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    const loadRoleMasters = async () => {
      if (!tenantId) {
        setRoleMasterOptions([]);
        return;
      }
      setRoleMastersLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}conductor/workforce-role-masters/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
          {
            method: 'GET',
            headers: authHeaders,
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to load position names: ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setRoleMasterOptions(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error loading workforce role masters:', error);
        if (!cancelled) setRoleMasterOptions([]);
      } finally {
        if (!cancelled) setRoleMastersLoading(false);
      }
    };
    loadRoleMasters();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, baseUrl, tenantId]);

  useEffect(() => {
    let cancelled = false;
    const loadDepartments = async () => {
      if (!tenantId) {
        setDepartmentOptions([]);
        return;
      }
      setDepartmentsLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}conductor/department-masters/tenant/${encodeURIComponent(tenantId)}`,
          { method: 'GET', headers: authHeaders }
        );
        if (!response.ok) {
          throw new Error(`Failed to load departments: ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setDepartmentOptions(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error loading departments:', error);
        if (!cancelled) setDepartmentOptions([]);
      } finally {
        if (!cancelled) setDepartmentsLoading(false);
      }
    };
    loadDepartments();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, baseUrl, tenantId]);

  useEffect(() => {
    let cancelled = false;
    const loadWorkAreas = async () => {
      setWorkAreaLoading(true);
      try {
        const response = await fetch(buildLookupListUrl(baseUrl, 'WORK_AREA', tenantId), {
          method: 'GET',
          headers: authHeaders,
        });
        if (!response.ok) {
          throw new Error(`Failed to load work areas: ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setWorkAreaOptions(parseLookupItems(data?.list_items ?? data?.listItems));
        }
      } catch (error) {
        console.error('Error loading work areas:', error);
        if (!cancelled) setWorkAreaOptions([]);
      } finally {
        if (!cancelled) setWorkAreaLoading(false);
      }
    };
    loadWorkAreas();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, baseUrl, tenantId]);

  const handleDeleteWorkAreaClick = (event, area) => {
    event.preventDefault();
    event.stopPropagation();
    if (!area?.code || area?.isNew) return;
    setWorkAreaPendingDelete({
      code: area.code,
      label: area.label || area.code,
    });
    setWorkAreaDeleteOpen(true);
  };

  const handleCloseWorkAreaDelete = () => {
    if (workAreaDeleteLoading) return;
    setWorkAreaDeleteOpen(false);
    setWorkAreaPendingDelete(null);
  };

  const handleConfirmDeleteWorkArea = async () => {
    if (!workAreaPendingDelete?.code) return;
    try {
      setWorkAreaDeleteLoading(true);
      const updatedList = await removeWorkAreaFromLookup({
        baseUrl,
        headers: authHeaders,
        tenantId,
        code: workAreaPendingDelete.code,
      });
      const nextOptions = parseLookupItems(updatedList?.list_items ?? updatedList?.listItems);
      setWorkAreaOptions(nextOptions);
      if (
        String(form.work_area_code || '').toUpperCase() ===
        String(workAreaPendingDelete.code).toUpperCase()
      ) {
        setForm((prev) => ({ ...prev, work_area_code: '', work_area_label: '' }));
      }
      showSuccess(`Work area "${workAreaPendingDelete.label}" deleted.`);
      setWorkAreaDeleteOpen(false);
      setWorkAreaPendingDelete(null);
    } catch (error) {
      console.error('Error deleting work area:', error);
      showError(error.message || 'Failed to delete work area');
    } finally {
      setWorkAreaDeleteLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!tenantId || !selectedPropertyId) {
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const roleRes = await fetch(
          `${baseUrl}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(selectedPropertyId)}`,
          { method: 'GET', headers: authHeaders }
        );
        const roleData = roleRes.ok ? await roleRes.json() : [];
        if (!roleRes.ok && roleRes.status) {
          const errorText = await roleRes.text().catch(() => '');
          console.error('Role positions fetch failed:', errorText || roleRes.status);
        }
        const nameLookup = Object.fromEntries(businessUnits.map((bu) => [bu.propertyId, formatBusinessUnitLabel(bu)]));
        if (cancelled) return;
        setRows(
          Array.isArray(roleData)
            ? roleData.map((item) => mapApiRolePositionToRow(item, nameLookup, workAreaLabelByCode))
            : []
        );
      } catch (error) {
        console.error('Error loading role positions:', error);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, selectedPropertyId, businessUnits, workAreaLabelByCode, authHeaders, baseUrl]);

  const handleOpenAdd = () => {
    if (!canAdd) {
      showError('You do not have permission to add positions', 'Access Denied');
      return;
    }
    setMode('add');
    setForm({
      ...defaultForm,
      property_id: selectedPropertyId,
      is_active: 'YES',
      is_salaried: 'NO',
      created_by: username,
      updated_by: username,
    });
    setEditingRolePositionId(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = () => {
    if (!canEdit || !hasSingleSelection) return;
    const row = rows.find((r) => r.id === selectedId);
    if (!row) return;
    setMode('edit');
    setEditingRolePositionId(row.role_position_uuid);
    setForm({
      ...defaultForm,
      ...row,
      effective_date: (row.effective_date || '').split(' ')[0],
      end_date: (row.end_date || '').split(' ')[0],
      updated_by: username,
    });
    setOpenDialog(true);
  };

  const handleOpenDeleteDialog = () => {
    if (!canDelete || !hasSelected) return;
    const selected = selectedRows.filter((row) => row.role_position_uuid);
    if (!selected.length) return;
    setRowsPendingDelete(selected);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setRowsPendingDelete([]);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || !rowsPendingDelete.length) return;

    setDeleteLoading(true);
    const idToRowId = new Map(rowsPendingDelete.map((row) => [String(row.role_position_uuid), row.id]));
    const ids = rowsPendingDelete.map((row) => row.role_position_uuid);

    try {
      const response = await fetch(`${baseUrl}conductor/role-positions/bulk-delete`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(ids),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete positions: ${response.status}`);
      }

      const result = await response.json();
      const succeeded = Array.isArray(result.succeeded) ? result.succeeded.map(String) : [];
      const failed = Array.isArray(result.failed) ? result.failed : [];
      const deletedRowIds = succeeded.map((id) => idToRowId.get(id)).filter(Boolean);

      if (deletedRowIds.length) {
        setRows((prev) => prev.filter((r) => !deletedRowIds.includes(r.id)));
        setSelectionModel((prev) => prev.filter((id) => !deletedRowIds.includes(id)));
      }

      setDeleteDialogOpen(false);
      setRowsPendingDelete([]);

      const okCount = result.successCount ?? succeeded.length;
      const failCount = result.failureCount ?? failed.length;

      if (failCount === 0) {
        showSuccess(okCount === 1 ? 'Position deleted successfully' : `${okCount} positions deleted successfully`);
      } else if (okCount === 0) {
        showError(failed.map((f) => f.error || 'Delete failed').join('; '));
      } else {
        showError(
          `Deleted ${okCount}, failed ${failCount}: ${failed.map((f) => f.error || 'Delete failed').join('; ')}`
        );
      }
    } catch (error) {
      console.error('Error bulk deleting positions:', error);
      showError(error.message || 'Failed to delete positions');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) {
      e.preventDefault();
      showError(
        mode === 'add'
          ? 'You do not have permission to add positions'
          : 'You do not have permission to edit positions',
        'Access Denied'
      );
      return;
    }
    e.preventDefault();
    try {
      setSubmitting(true);

      let areaLabels = workAreaLabelByCode;
      if (form.work_area_code) {
        const alreadyKnown = workAreaOptions.some(
          (area) => String(area.code || '').toUpperCase() === String(form.work_area_code).toUpperCase()
        );
        if (!alreadyKnown) {
          const updatedList = await ensureWorkAreaInLookup({
            baseUrl,
            headers: authHeaders,
            tenantId,
            code: form.work_area_code,
            label: form.work_area_label || form.work_area_code,
          });
          const nextOptions = parseLookupItems(updatedList?.list_items ?? updatedList?.listItems);
          setWorkAreaOptions(nextOptions);
          areaLabels = buildWorkAreaLabelMap(nextOptions);
        }
      }

      const payload = buildPayload(form, mode === 'edit' ? editingRolePositionId : null);
      const url =
        mode === 'edit'
          ? `${baseUrl}conductor/role-positions/${editingRolePositionId}`
          : `${baseUrl}conductor/role-positions`;
      const response = await fetch(url, {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save position: ${response.status}`);
      }

      const saved = await response.json();
      const mapped = mapApiRolePositionToRow(saved, businessUnitNameById, areaLabels);

      if (mode === 'edit') {
        setRows((prev) => prev.map((r) => (r.id === mapped.id ? mapped : r)));
      } else {
        setRows((prev) => [mapped, ...prev]);
      }
      setOpenDialog(false);
      showSuccess(mode === 'edit' ? 'Position updated successfully' : 'Position saved successfully');
    } catch (error) {
      console.error('Error saving role position:', error);
      showError(error.message || 'Failed to save position');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleRowClick = (params) => {
    setSelectedDetailsRow(params.row);
    setDetailsDrawerOpen(true);
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ fontSize: '0.9rem', '& .MuiBreadcrumbs-separator': { color: ht.white } }}
        >
          <Link
            underline="hover"
            onClick={onBack}
            sx={{
              cursor: 'pointer',
              color: ht.white,
              '&:hover': { color: ht.white },
            }}
          >
            Workforce Operations
          </Link>
          <Typography sx={{ color: ht.white }}>
            {selectedBusinessUnitLabel ? `Position - ${selectedBusinessUnitLabel}` : 'Position'}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Position
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canAdd ? 'Add Position' : "You don't have permission to add positions"}>
            <span>
              <IconButton
                sx={harmonatePlainIconButtonSx}
                onClick={handleOpenAdd}
                disabled={!canAdd}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!canEdit ? 'No edit permission' : hasSingleSelection ? 'Edit Position' : hasSelected ? 'Select one position to edit' : 'Edit Position'}>
            <span>
              <IconButton sx={harmonatePlainIconButtonSx} disabled={!hasSingleSelection || !canEdit} onClick={handleOpenEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!canDelete ? 'No delete permission' : hasSelected && selectedRows.length > 1 ? `Delete ${selectedRows.length} Positions` : 'Delete Position'}>
            <span>
              <IconButton sx={harmonatePlainIconButtonSx} disabled={!hasSelected || !canDelete} onClick={handleOpenDeleteDialog}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          mb: 3,
          height: 500,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={40}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(m) => setSelectionModel(m)}
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          loading={loading}
          onRowClick={handleRoleRowClick}
          sx={{
            ...harmonateDataGridSx,
            '& .MuiDataGrid-row:hover': { ...harmonateDataGridSx['& .MuiDataGrid-row:hover'], cursor: 'pointer' },
          }}
        />
      </Paper>

      <PositionDetailsDrawer
        open={detailsDrawerOpen}
        onClose={() => setDetailsDrawerOpen(false)}
        positionRow={selectedDetailsRow}
        selectedBusinessUnit={selectedBusinessUnit}
        token={token}
        baseUrl={baseUrl}
        tenantId={tenantId}
        propertyId={selectedPropertyId}
        showError={showError}
        showWarning={showWarning}
        showSuccess={showSuccess}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Position' : 'Edit Position'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <Typography variant="subtitle2" sx={{ color: ht.text, fontWeight: 600 }}>
              Position Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Business Unit"
                name="property_id"
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                size="small"
                fullWidth
                select
                disabled
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="">
                  <em>Select business unit</em>
                </MenuItem>
                {businessUnits.map((bu) => (
                  <MenuItem key={bu.propertyId} value={bu.propertyId}>
                    {formatBusinessUnitLabel(bu)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Job Title"
                name="role_name"
                value={form.role_name}
                onChange={(e) => {
                  const name = e.target.value;
                  const master = roleMasterOptions.find(
                    (item) => (item.roleName || item.role_name) === name
                  );
                  const masterCode = master?.roleCode || master?.role_code || '';
                  setForm((prev) => ({
                    ...prev,
                    role_name: name,
                    ...(mode === 'add'
                      ? { role_code: masterCode || (name ? slugifyRoleCode(name) : prev.role_code) }
                      : {}),
                  }));
                }}
                size="small"
                fullWidth
                required
                select
                disabled={roleMastersLoading}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="">
                  <em>Select job title</em>
                </MenuItem>
                {roleMasterNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Job Code"
                name="role_code"
                value={form.role_code}
                onChange={(e) => setForm({ ...form, role_code: e.target.value })}
                size="small"
                fullWidth
                required
              />
              <TextField
                label="Department"
                name="department"
                value={form.department}
                onChange={(e) => {
                  const name = e.target.value;
                  const selected = departmentNames.find((item) => item.name === name);
                  setForm((prev) => ({
                    ...prev,
                    department: name,
                    department_code: selected?.code || '',
                  }));
                }}
                size="small"
                fullWidth
                select
                disabled={departmentsLoading}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="">
                  <em>Select department</em>
                </MenuItem>
                {departmentNames.map((item) => (
                  <MenuItem key={item.name} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Department Code"
                name="department_code"
                value={form.department_code}
                size="small"
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
              <Autocomplete
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                fullWidth
                size="small"
                loading={workAreaLoading}
                options={workAreaOptions}
                value={
                  form.work_area_code
                    ? workAreaOptions.find(
                        (area) =>
                          String(area.code || '').toUpperCase() ===
                          String(form.work_area_code).toUpperCase()
                      ) || {
                        code: form.work_area_code,
                        label: form.work_area_label || form.work_area_code,
                      }
                    : null
                }
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.label || option.code || '';
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option?.code || '').toUpperCase() === String(value?.code || '').toUpperCase()
                }
                filterOptions={(options, params) => {
                  const filtered = workAreaFilter(options, params);
                  const input = (params.inputValue || '').trim();
                  if (!input) return filtered;
                  const exists = options.some(
                    (opt) =>
                      String(opt.label || '').toLowerCase() === input.toLowerCase() ||
                      String(opt.code || '').toLowerCase() === input.toLowerCase()
                  );
                  if (!exists) {
                    filtered.push({
                      inputValue: input,
                      code: slugifyWorkAreaCode(input),
                      label: `Add "${input}"`,
                      newLabel: input,
                      isNew: true,
                    });
                  }
                  return filtered;
                }}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setForm((prev) => ({ ...prev, work_area_code: '', work_area_label: '' }));
                    return;
                  }
                  if (typeof newValue === 'string') {
                    const label = newValue.trim();
                    setForm((prev) => ({
                      ...prev,
                      work_area_code: slugifyWorkAreaCode(label),
                      work_area_label: label,
                    }));
                    return;
                  }
                  if (newValue.isNew || newValue.inputValue) {
                    const label = (newValue.newLabel || newValue.inputValue || '').trim();
                    setForm((prev) => ({
                      ...prev,
                      work_area_code: newValue.code || slugifyWorkAreaCode(label),
                      work_area_label: label,
                    }));
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    work_area_code: newValue.code || '',
                    work_area_label: newValue.label || newValue.code || '',
                  }));
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.code || option.inputValue || option.label}
                    style={{
                      ...props.style,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span>{option.isNew ? option.label : option.label || option.code}</span>
                    {!option.isNew && option.code ? (
                      <IconButton
                        size="small"
                        aria-label={`Delete work area ${option.label || option.code}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => handleDeleteWorkAreaClick(e, option)}
                        sx={{ color: '#d32f2f', p: 0.25 }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    ) : null}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Work Area"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Select or type a new work area"
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Position Type"
                name="role_type"
                value={form.role_type}
                onChange={(e) => setForm({ ...form, role_type: e.target.value })}
                size="small"
                fullWidth
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="CORE">CORE</MenuItem>
                <MenuItem value="SUPPORT">SUPPORT</MenuItem>
                <MenuItem value="MANAGEMENT">MANAGEMENT</MenuItem>
              </TextField>
              <TextField
                label="Color Code"
                name="color_code"
                value={form.color_code}
                onChange={(e) => setForm({ ...form, color_code: e.target.value })}
                size="small"
                fullWidth
              />
            </Box>
            <TextField
              label="Priority"
              name="priority"
              type="number"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              size="small"
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Is Salaried"
                name="is_salaried"
                value={form.is_salaried}
                onChange={(e) => setForm({ ...form, is_salaried: e.target.value })}
                size="small"
                fullWidth
                select
              >
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
              </TextField>
              <TextField
                label="Is Active"
                name="is_active"
                value={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.value })}
                size="small"
                fullWidth
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
              </TextField>
            </Box>
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              size="small"
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDialog(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {rowsPendingDelete.length <= 1 ? (
            <Typography sx={{ color: ht.text }}>
              Are you sure you want to delete the position &quot;
              {rowsPendingDelete[0]?.role_name || rowsPendingDelete[0]?.role_code || 'selected'}
              &quot;?
            </Typography>
          ) : (
            <Typography sx={{ color: ht.text }}>
              Are you sure you want to delete <strong>{rowsPendingDelete.length}</strong> selected positions?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading
              ? 'Deleting...'
              : rowsPendingDelete.length > 1
                ? `Delete ${rowsPendingDelete.length}`
                : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={workAreaDeleteOpen}
        onClose={handleCloseWorkAreaDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete work area &quot;
            {workAreaPendingDelete?.label || workAreaPendingDelete?.code || 'selected'}
            &quot; from the list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWorkAreaDelete} disabled={workAreaDeleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteWorkArea}
            variant="contained"
            disabled={workAreaDeleteLoading}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {workAreaDeleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </>
  );
};

export default RolePosition;
